import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppStore } from '@store/appStore';
import { apiClient } from '@/lib/api';
import { ROUTES } from '@/constants';
import { Skeleton } from '@components/ui/Skeleton';
import { formatUSPhone } from '../utils';
import AddPassengerDrawer from '@/features/trip-sheet/components/AddPassengerDrawer';
import QuoteCard from '@/features/quotes/components/QuoteCard';
import {
  RiContactsBook2Line,
  RiBuildingLine,
  RiMailLine,
  RiPhoneLine,
  RiListCheck3,
  RiTeamLine,
  RiTimeLine,
  RiUserAddLine,
} from '@remixicon/react';

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const initialClient = location.state?.client || null;

  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);

  const { data: client, isLoading, isFetching: isClientFetching } = useQuery({
    queryKey: ['client', id],
    queryFn: () => apiClient.getClientById(id, { include: 'quotes,quotes.bids' }),
    enabled: !!id,
    initialData: initialClient,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: passengersData = [], isLoading: isPassengersLoading } = useQuery({
    queryKey: ['clientPassengers', id],
    queryFn: () => apiClient.getClientPassengers(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const name = client ? [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Unknown' : '';
  const company = client?.company_name || client?.company || '';

  // Set page header in AppLayout
  useEffect(() => {
    if (client) {
      setPageHeaderOverride({
        title: name,
        subtitle: company || undefined,
        onEdit: () => navigate(`/clients/${id}/edit`, { state: { client } }),
      });
    }
    return () => clearPageHeaderOverride();
  }, [client, name, company, id, navigate, setPageHeaderOverride, clearPageHeaderOverride]);
  const email = client?.email || '';
  const phone = client?.contact_numbers?.[0] || client?.phone || '';
  const operationalPreferences = client?.operational_preferences || '';
  const passengers = Array.isArray(passengersData) ? passengersData : (passengersData?.data || []);
  const quotes = client?.quotes || [];
  const isQuotesLoading = isClientFetching && quotes.length === 0;


  const renameWithClientId = (clientId, file) => {
    const client = clientId || 'unknown';
    const newName = `${client}_${file.name}`;
    return new File([file], newName, { type: file.type });
  };

  const handleAddPassenger = async (passenger) => {
    try {
      const clientId = client.client_id || client.id;

      let passport_url = passenger.existingPassportUrl || null;
      let visa_url = passenger.existingVisaUrl || null;
      if (passenger.passportFiles?.length) {
        const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, passenger.passportFiles[0]), "passport");
        passport_url = uploaded?.file_path || null;
      }
      if (passenger.visaFiles?.length) {
        const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, passenger.visaFiles[0]), "visa");
        visa_url = uploaded?.file_path || null;
      }

      const dob = passenger.dateOfBirth
        ? passenger.dateOfBirth.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$1-$2')
        : null;

      const mapped = {
        full_name: passenger.fullName || '',
        date_of_birth: dob,
        nationality: passenger.nationality || null,
        passport_num: null,
        passport_url,
        visa_url,
        weight: passenger.weight || null,
        additional_info: {
          dietary_catering: passenger.dietaryCatering || null,
          cabin_environment: passenger.cabinEnvironment || null,
          medical_mobility: passenger.medicalMobility || null,
          pet_accommodations: passenger.petAccommodations || null,
        },
      };

      const passengerId = editingPassenger?.client_passenger_id || editingPassenger?.id;
      if (passengerId) {
        await apiClient.updateClientPassenger(passengerId, mapped);
        toast.success('Passenger updated successfully');
      } else {
        await apiClient.createClientPassengersBatch(clientId, [mapped]);
        toast.success('Passenger added successfully');
      }

      queryClient.invalidateQueries({ queryKey: ['clientPassengers', id] });
      setPassengerDrawerOpen(false);
      setEditingPassenger(null);
    } catch (err) {
      console.error('Failed to save passenger:', err);
      toast.error('Failed to save passenger');
    }
  };

  const handleRemovePassenger = async () => {
    try {
      const passengerId = editingPassenger?.client_passenger_id || editingPassenger?.id;
      if (passengerId) {
        await apiClient.deleteClientPassenger(passengerId);
        toast.success('Passenger removed successfully');
        queryClient.invalidateQueries({ queryKey: ['clientPassengers', id] });
        setEditingPassenger(null);
        setPassengerDrawerOpen(false);
      }
    } catch (err) {
      console.error('Failed to remove passenger:', err);
      toast.error('Failed to remove passenger');
      setEditingPassenger(null);
      setPassengerDrawerOpen(false);
    }
  };

  if (isLoading && !initialClient) {
    return (
      <div className="pb-8 max-w-2xl space-y-4">
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Client not found</p>
        <button
          onClick={() => navigate(ROUTES.CLIENTS.path)}
          className="mt-4 text-primary-600 text-sm font-medium"
        >
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="pb-8 max-w-2xl space-y-4">
      {/* Contact Details */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-5 pb-3 border-b boder-gray-20 mb-2">
          <RiContactsBook2Line className="w-5 h-5 text-gray-500" />
          <span className="text-md font-medium text-gray-900">Contact Details</span>
        </div>
        <div className="px-5 pb-5 space-y-4">
          <h3 className="text-2xl font-semibold text-gray-900">{name}</h3>
          {company && (
            <div className="flex items-start gap-3">
              <div className="bg-gray-100 p-2 rounded-full">
              <RiBuildingLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Company</p>
                <p className="text-md text-gray-900 font-medium">{company}</p>
              </div>
            </div>
          )}
          {email && (
            <div className="flex items-start gap-3">
              <div className="bg-gray-100 p-2 rounded-full">
              <RiMailLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-md text-gray-900 font-medium">{email}</p>
              </div>
            </div>
          )}
          {phone && (
            <div className="flex items-start gap-3">
              <div className="bg-gray-100 p-2 rounded-full">
              <RiPhoneLine className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Phone</p>
                <p className="text-md text-gray-900 font-medium">{formatUSPhone(phone)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Operational Preferences */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-5 pb-3 border-b boder-gray-20 mb-2">
          <RiListCheck3 className="w-5 h-5 text-gray-500" />
          <span className="text-md font-medium text-gray-900">Operational Preferences</span>
        </div>
        <div className="px-5 pb-5">
          {operationalPreferences ? (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{operationalPreferences}</p>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              Add client's operational preferences by{'\n'}tapping the edit button at the top.
            </p>
          )}
        </div>
      </div>

      {/* Saved Passengers */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-5 pb-3 border-b boder-gray-20 mb-2">
          <RiTeamLine className="w-5 h-5 text-gray-500" />
          <span className="text-md font-medium text-gray-900">Saved Passengers</span>
        </div>
        <div className="px-5 pb-5">
          {isPassengersLoading ? (
            <div className="space-y-2 mb-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32 rounded" />
                    <Skeleton className="h-3 w-20 rounded" />
                  </div>
                  <Skeleton className="h-4 w-8 rounded" />
                </div>
              ))}
            </div>
          ) : passengers.length > 0 ? (
            <div className="space-y-2 mb-4">
              {passengers.map((p, i) => (
                <div
                  key={p.id || i}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ')}
                    </p>
                    {p.nationality && (
                      <p className="text-xs text-gray-400">{p.nationality}</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setEditingPassenger(p);
                      setPassengerDrawerOpen(true);
                    }}
                    className="text-xs text-primary-600 font-medium"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4 mb-4">
              No passengers added yet
            </p>
          )}
          <button
            onClick={() => {
              setEditingPassenger(null);
              setPassengerDrawerOpen(true);
            }}
            className="w-full flex items-center justify-center gap-2 py-3 border border-gray-200 text-sm font-medium text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
          >
            <RiUserAddLine className="w-4 h-4" />
            Add Passenger
          </button>
        </div>
      </div>

      {/* Quote History */}
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-5 pb-3  border-b boder-gray-20 mb-2">
          <RiTimeLine className="w-5 h-5 text-gray-500" />
          <span className="text-md font-medium text-gray-900">Quote History</span>
        </div>
        <div className="px-5 pb-5">
          {isQuotesLoading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-xl space-y-2">
                  <Skeleton className="h-4 w-40 rounded" />
                  <Skeleton className="h-3 w-56 rounded" />
                  <Skeleton className="h-3 w-24 rounded" />
                </div>
              ))}
            </div>
          ) : quotes.length > 0 ? (
            <div className="space-y-3">
              {quotes.map((quote, i) => (
                <QuoteCard key={quote.id || quote.quote_id || i} quote={quote} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">
              No quotes yet
            </p>
          )}
        </div>
      </div>

      {/* Add Passenger Drawer */}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => {
          setPassengerDrawerOpen(false);
          setEditingPassenger(null);
        }}
        onAdd={handleAddPassenger}
        onRemove={handleRemovePassenger}
        editingPassenger={editingPassenger}
        clientId={client.client_id || client.id}
        newOnly
      />
    </div>
  );
};

export default ClientDetail;
